import type { TieredSelectProps } from '~/features/common';
import type { SavedContent } from '../../types';
import type { PaginationProps } from '@workramp/ui';
import { Row, Col, Stack, SubHeading, Pagination, Body } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import { TieredSelect } from '~/features/common';
import { SavedContentCard } from '../SavedContentCard';
import { SavedContentProvider } from '../SavedContentProvider';
import { SAVED_CONTENT_PER_PAGE } from '../../constants';

export interface SavedContentViewProps {
  savedContent: SavedContent[];
  sortOptions: TieredSelectProps['options'];
  sortSelection: TieredSelectProps['selection'];
  page: number;
  totalItems: number;
  onSortChange: TieredSelectProps['onSelectionChange'];
  onPageChange: PaginationProps['onPageChange'];
}

const SAVED_CONTENT = I18n.t('js.learner.saved_content.saved_content');
const SORT_BY_LABEL = I18n.t('js.learner.saved_content.sort.sort_by_label');
const SORT_DIR_LABEL = I18n.t('js.learner.saved_content.sort.sort_dir_label');
const EMPTY_MESSAGE = I18n.t('js.learner.saved_content.empty_message');
const EMPTY_MESSAGE_DESCRIPTION = I18n.t('js.learner.saved_content.empty_message_description');

export function SavedContentView({
  savedContent,
  sortOptions,
  sortSelection,
  page,
  totalItems,
  onSortChange,
  onPageChange,
}: SavedContentViewProps) {
  return (
    <Stack size={4}>
      <Row align="center">
        <Col>
          <SubHeading level="xl">{SAVED_CONTENT}</SubHeading>
        </Col>
        <Col xs="auto">
          <TieredSelect
            primaryTitle={SORT_BY_LABEL}
            secondaryTitle={SORT_DIR_LABEL}
            options={sortOptions}
            selection={sortSelection}
            onSelectionChange={onSortChange}
          />
        </Col>
      </Row>

      <SavedContentProvider contentIds={savedContent.map(({ content }) => content.id)}>
        {savedContent.length > 0 && (
          <Row gapY={6}>
            {savedContent.map((content) => (
              <Col xs={6} md={4} lg={3} xl={2} key={content.id}>
                <SavedContentCard content={content} />
              </Col>
            ))}
          </Row>
        )}
        {savedContent.length === 0 && (
          <Stack size={2} className="text-center">
            <Row>
              <Col>
                <SubHeading level="lg">{EMPTY_MESSAGE}</SubHeading>
              </Col>
            </Row>
            <Row>
              <Col>
                <Body level="lg">{EMPTY_MESSAGE_DESCRIPTION}</Body>
              </Col>
            </Row>
          </Stack>
        )}
      </SavedContentProvider>

      {totalItems > SAVED_CONTENT_PER_PAGE && (
        <Row>
          <Col>
            <Pagination
              allowsItemsPerPage={false}
              itemsPerPage={SAVED_CONTENT_PER_PAGE}
              page={page}
              totalItems={totalItems}
              onPageChange={onPageChange}
            />
          </Col>
        </Row>
      )}
    </Stack>
  );
}
